/** Mirrors backend PTBookingAction — latest action on a PT booking. */
export enum PTBookingAction {
  Unset = 0,
  Book = 1,
  Cancel = 2,
  CheckIn = 3,
  NoShow = 4,
  LateCancel = 5,
  ConfirmedCheckIn = 6
}

/** Cancel and LateCancel both mean the session was cancelled. */
export function isCancelledPtBookingAction(action: number | null | undefined): boolean {
  return action === PTBookingAction.Cancel || action === PTBookingAction.LateCancel;
}

export class Session {
  Id?: number;
  Member?: any;
  MemberId?: number;
  TrainerId?: number;
  ScheduleId?: number;
  StartDate: string = '';
  StartTime: string = '';
  EndTime: string = '';
  StartDateTime?: string;
  Type: number = 1;
  Duration?: number = 0;
  Action: number = 0;
}

export enum CurrencyType {
  Unset = 0,
  Regular = 1,
  StoreCredit = 2,
  PersonalTrainingFlexible = 3,
  GroupTrainingFlexible = 4,
  PersonalTrainingStrict = 5,
  GroupTrainingStrict = 6,
  FitnessAssessment = 7
}

/** Currency types that count as PT (private training). Used to filter balances for Individual PT sessions. */
export const PT_CURRENCY_TYPES: CurrencyType[] = [
  CurrencyType.PersonalTrainingFlexible,
  CurrencyType.PersonalTrainingStrict
];

/** Currency types that count as Group PT. Used to filter balances for Group PT sessions. */
export const GROUP_PT_CURRENCY_TYPES: CurrencyType[] = [
  CurrencyType.GroupTrainingFlexible,
  CurrencyType.GroupTrainingStrict
];

export interface CurrencyBalance {
  CurrencyId: number;
  CurrencyName?: string;
  Balance: number;
  Scale: number;
  ProductUnit: string;
  Type: CurrencyType;
}

export class Client {
  Id: number = 0;
  PersonId: number = 0;
  ScheduleId: number = 0;
  Avatar?: any;
  /** @deprecated Use CurrencyBalances with Type in GROUP_PT_CURRENCY_TYPES instead */
  GroupSessions: number = 0;
  /** @deprecated Use CurrencyBalances with Type in PT_CURRENCY_TYPES instead */
  PTSessions: number = 0;
  CurrencyBalances?: CurrencyBalance[];
  Person?: any;
  State: number = 0;
}

export enum Tier {
  Unset = 0,
  Tier1 = 1,
  Tier2 = 2
}

export enum Action {
  Unset = 0,
  Add = 1,
  Remove = 2
}

/** Booking session type sent to API. Backend resolves currency via PT (Flexible/Strict) or Group PT (Flexible/Strict) types. */
export enum BookingSessionType {
  IndividualPT = 1,
  GroupPT = 2
}

export enum UserState {
    Inactive = 0,
    Active = 1,
    Deleted = 2,
    Unavailable = 3
}

export enum UploadIntent {
  Unset = 0,
  Member = 1,
  Instructor = 2,
  Trainer = 3,
  Staff = 4,
  Facility = 5,
  Class = 6,
  Studio = 7,
  Cafe = 8,
  Currency = 9
}

export interface BaseModel {
  Id: number;
}

export interface TimeStampModel extends GuidModel {
    CreatedAt: string;
    CreatedBy: string;
}

export interface TimeStampedModel extends GuidModel {
  CreatedAt: string;
  CreatedBy: string;
  LastModified: string;
  LastModifiedBy: string;
}

export interface GuidModel extends BaseModel {
    Guid: string;
}

export interface Trainer extends TimeStampModel {
    PersonId: number;
    PersonGuid: string;
    Tier: Tier;
    SSN: string;
    GustoEmployeeId: string;
    State: UserState;
    Order: number;
}

export interface Person extends TimeStampedModel {
    FirstName: string;
    LastName: string;
    PreferredName: string;
    Gender: string;
    BirthDate: string;
    DialCode: string;
    PhoneNumber: string;
    Email: string;
    ZipCode: string;
    FullName: string;
    FullDetail: string;
    Initials: string;
    FirstInitial: string;
    LastInitial: string;
    PhotoUrl: string;
    GenderStr: string;
}

export interface Upload extends TimeStampedModel {
    Name: string;
    Intent: UploadIntent;
    IntentId: number;
    Description: string;
    MimeType: string;
    UploadedUrl: string;
    UploadTypeId: number;
    UploadQualityId: number | null;
    Version: number;
    IsActive: boolean;
    LocalPath: string;
}

export interface TrainerSetting extends TimeStampedModel {
    TrainerId: number;
    Key: string;
    Value: string;
    Action: Action;
}

export interface TrainerModel extends Trainer {
    Person: Person;
    Avatar: Upload;
    Settings: TrainerSetting[];
}